/**
 * Shared HTML preview stub functions.
 *
 * Used by both the LP builder (HtmlPreview) and the asset review viewer
 * (AssetDocumentViewer) to make generated HTML safe for sandboxed iframe rendering.
 */

/** Stub localStorage, sessionStorage, and document.cookie so scripts don't crash in the sandboxed iframe. */
export const injectSandboxStubs = (rawHtml: string): string => {
    const stub = `<script>(function(){var ns={getItem:function(){return null},setItem:function(){},removeItem:function(){},clear:function(){},key:function(){return null},length:0};try{window.localStorage}catch(e){Object.defineProperty(window,'localStorage',{value:ns})}try{window.sessionStorage}catch(e){Object.defineProperty(window,'sessionStorage',{value:ns})}try{document.cookie}catch(e){Object.defineProperty(document,'cookie',{get:function(){return''},set:function(){}})}})();</script>`
    // Match `<head>` tolerantly: any case, optional attributes, optional whitespace.
    // AI-generated HTML frequently emits e.g. `<head lang="en">` or `<HEAD>`.
    const headOpenTag = /<head\b[^>]*>/i
    return headOpenTag.test(rawHtml)
        ? rawHtml.replace(headOpenTag, match => `${match}${stub}`)
        : stub + rawHtml
}

/** Replace YouTube iframe embeds with a static thumbnail + play button for sandboxed preview. */
export const replaceYouTubeEmbeds = (rawHtml: string): string =>
    rawHtml.replace(
        /<iframe[^>]*\ssrc=["']https?:\/\/(?:www\.)?(?:youtube\.com|youtube-nocookie\.com)\/embed\/([a-zA-Z0-9_-]+)[^"']*["'][^>]*(?:\/>|>[\s\S]*?<\/iframe>)/gi,
        (_match, videoId: string) =>
            `<div style="position:absolute;inset:0;background:#000;overflow:hidden">`
            + `<img src="https://img.youtube.com/vi/${videoId}/hqdefault.jpg" style="width:100%;height:100%;object-fit:cover" alt="Video thumbnail"/>`
            + `<div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center">`
            + `<svg width="68" height="48" viewBox="0 0 68 48"><path d="M66.5 7.7s-.7-4.7-2.7-6.8C61 -.1 58 -.1 56.6-.3 47.3-1 34-1 34-1s-13.3 0-22.6.7C10 0 7 0 4.2.9 2.2 3 1.5 7.7 1.5 7.7S.8 13.2.8 18.6v5.1c0 5.4.7 10.9.7 10.9s.7 4.7 2.7 6.8c2.8 2.9 6.4 2.8 8 3.1 5.8.6 24.8.8 24.8.8s13.3 0 22.6-.8c1.4-.2 4.4-.2 7.2-3.1 2-2.1 2.7-6.8 2.7-6.8s.7-5.4.7-10.9v-5.1c0-5.4-.7-10.9-.7-10.9z" fill="#212121" fill-opacity=".8"/><path d="M45 24L27 14v20" fill="#fff"/></svg>`
            + `<span style="margin-top:12px;font-size:12px;color:#fff;background:rgba(0,0,0,.6);padding:4px 10px;border-radius:4px">Video preview — plays on published page</span>`
            + `</div></div>`,
    )

/** Replace download links (<a download>) with a static placeholder for sandboxed preview. */
export const replaceDownloadLinks = (rawHtml: string): string =>
    rawHtml.replace(
        /<a\b[^>]*\sdownload(?:\s|=|>)[^>]*>([\s\S]*?)<\/a>/gi,
        () =>
            `<span style="display:inline-flex;align-items:center;gap:8px;padding:10px 20px;background:#f1f5f9;border:2px dashed #cbd5e1;border-radius:8px;font-family:system-ui,sans-serif;cursor:default">`
            + `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`
            + `<span style="font-size:13px;color:#334155;font-weight:500">Download</span>`
            + `<span style="font-size:11px;color:#94a3b8;background:#e2e8f0;padding:2px 8px;border-radius:4px">Available on published page</span>`
            + `</span>`,
    )

/** Replace Calendly inline-widget embeds with a static placeholder for sandboxed preview and strip the widget.js script tag. */
export const replaceCalendlyEmbeds = (rawHtml: string): string =>
    rawHtml
        .replace(
            /<div[^>]*class="[^"]*calendly-inline-widget[^"]*"[^>]*data-url="([^"]*)"[^>]*>[\s\S]*?<\/div>/gi,
            (_match, url: string) =>
                `<div style="min-width:320px;height:700px;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f8fafc;border:2px dashed #cbd5e1;border-radius:12px;font-family:system-ui,sans-serif">`
                + `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/></svg>`
                + `<span style="margin-top:12px;font-size:14px;font-weight:600;color:#334155">Calendly Scheduling</span>`
                + `<span style="margin-top:6px;font-size:12px;color:#64748b;max-width:320px;text-align:center;word-break:break-all;padding:0 16px">${url}</span>`
                + `<span style="margin-top:10px;font-size:11px;color:#94a3b8;background:#f1f5f9;padding:4px 10px;border-radius:4px">Available on published page</span>`
                + `</div>`,
        )
        .replace(/<script[^>]*src="[^"]*assets\.calendly\.com[^"]*"[^>]*><\/script>/gi, '')

/** The event runtime's own state names — see EventRuntimeScriptBuilder on the API side. */
export type EventState = 'pre' | 'live' | 'post'

/**
 * Pins the event runtime to one state for the preview, so an author can see the post-event page
 * before the event has happened. The name is the runtime's contract (EventRuntimeScriptBuilder) and
 * the values are its own 'pre' | 'live' | 'post'. It replaced `window.__FORCE_STATE`, which only the
 * superseded AI-authored date JavaScript ever read, so the toggle moved nothing on a page built to
 * the current contract. Unpinned, the runtime picks the state from the clock.
 */
export const injectForcedEventState = (rawHtml: string, state: EventState): string => {
    const script = `<script>window.__OX_EVENT_STATE='${state}';</script>`
    return rawHtml.includes('</head>')
        ? rawHtml.replace('</head>', `${script}</head>`)
        : `${script}${rawHtml}`
}

/** Apply all preview stubs to raw HTML for safe sandboxed rendering. */
export function prepareHtmlForPreview(rawHtml: string): string {
    let html = rawHtml
    html = replaceYouTubeEmbeds(html)
    html = replaceDownloadLinks(html)
    html = replaceCalendlyEmbeds(html)
    html = injectSandboxStubs(html)
    return html
}
